import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Clock, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Clickable, EmptyState, ErrorBanner, Loader, PageHeader, StatusBadge } from "@/components/common";
import { DeleteButton } from "@/components/DeleteButton";
import { fmtDateTime, fmtMinutes, fmtRelative } from "@/lib/format";
import { FRONT_OFFICE, STATUSES } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export default function WorkshopPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "open";
  const { data, loading, error, reload } = useFetch(() => api.get("/repair-orders"), []);

  const counts = useMemo(() => {
    const c = { open: 0, all: (data || []).length };
    (data || []).forEach((r) => {
      c[r.status] = (c[r.status] || 0) + 1;
      if (!["returned", "cancelled"].includes(r.status)) c.open += 1;
    });
    return c;
  }, [data]);

  const list = (data || []).filter((r) => (status === "open" ? !["returned", "cancelled"].includes(r.status) : status === "all" ? true : r.status === status));

  return (
    <div data-testid="workshop-page">
      <PageHeader title="Atelier — Ordres de réparation" subtitle="Suivi de tous les dossiers, du rendez-vous à la restitution" actions={FRONT_OFFICE.includes(user.role) && <Button onClick={() => navigate("/atelier/nouveau")} className="rounded-full" data-testid="new-reception-button"><PlusCircle className="mr-2 h-4 w-4" /> Nouvelle réception</Button>} />
      <div className="mb-5 flex flex-wrap gap-2" data-testid="status-filters">
        {[{ id: "open", label: "Dossiers ouverts" }, ...STATUSES, { id: "all", label: "Tous" }].map((s) => (
          <button key={s.id} onClick={() => setParams(s.id === "open" ? {} : { status: s.id })} data-testid={`status-filter-${s.id}`} className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors", status === s.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/50")}>
            {s.label} <span className="ml-1 opacity-70 tabular">{counts[s.id] ?? 0}</span>
          </button>
        ))}
      </div>
      {loading && !data ? <Loader /> : error ? <ErrorBanner message={error} onRetry={reload} /> : list.length === 0 ? <EmptyState title="Aucun dossier dans cette vue" /> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {list.map((ro, i) => (
            <Clickable key={ro.id} onClick={() => navigate(`/atelier/${ro.id}`)} testId={`ro-card-${ro.number}`} style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }} className="surface surface-hover rise flex flex-col gap-3 p-4 text-left">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="tabular text-xs font-bold text-primary">{ro.number}</div>
                  <div className="truncate text-base font-semibold">{ro.vehicle_label}</div>
                  <div className="truncate text-sm text-muted-foreground">{ro.client_name}</div>
                </div>
                <div className="flex items-center gap-1"><StatusBadge status={ro.status} /><DeleteButton url={`/repair-orders/${ro.id}`} label={`le dossier ${ro.number}`} description="Le dossier, ses devis, factures et demandes de pièces seront supprimés définitivement." onDeleted={reload} testId={`ro-delete-${ro.number}`} /></div>
              </div>
              <p className="line-clamp-2 text-sm text-muted-foreground">« {ro.reception?.reason} »</p>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full bg-muted px-2 py-0.5 tabular">{ro.flags.tasks_done}/{ro.flags.tasks_total} tâches</span>
                {ro.mechanic_names.map((m) => <span key={m} className="rounded-full border border-border px-2 py-0.5">{m}</span>)}
                {ro.flags.overtime && <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 font-semibold text-red-500"><Clock className="h-3 w-3" /> Dépassement</span>}
                {ro.flags.late_risk && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 font-semibold text-amber-500"><AlertTriangle className="h-3 w-3" /> Retard possible</span>}
              </div>
              <div className="flex items-center justify-between border-t border-border/60 pt-2 text-xs text-muted-foreground">
                <span>Reçu {fmtRelative(ro.reception?.received_at || ro.created_at)}</span>
                <span>{ro.reception?.promised_date ? `Restitution ${fmtDateTime(ro.reception.promised_date)}` : "Restitution non fixée"}</span>
              </div>
              {ro.blocked_reason && <div className="rounded-lg bg-orange-500/10 px-2 py-1 text-xs text-orange-500">{ro.blocked_reason}</div>}
              {ro.flags.active_task && <div className="text-xs"><span className="text-muted-foreground">En cours : </span>{ro.flags.active_task} · {fmtMinutes(ro.flags.work_minutes)}</div>}
            </Clickable>
          ))}
        </div>
      )}
    </div>
  );
}
